import React, { useState } from "react";
import {
  Modal,
  Box,
  Typography,
  FormControl,
  TextField,
  Button,
  Autocomplete,
  Chip,
  CircularProgress,
} from "@mui/material";
import Pdf from "../../images/managedata/pdf.svg";
import Word from "../../images/managedata/word.svg";
import Csv from "../../images/managedata/csv.svg";
import axios from "axios";
import config from "../../config.js";

export default function EmailReportModal({
  open,
  onClose,
  organizationUsers,
  reportType,
  exportFormat,
  reportCategory,
  dateOptions,
  selectedCategories,
  selectedSiteId,
}) {
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [emailError, setEmailError] = useState("");
  const [sending, setSending] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "",
  });

  // Email validation function
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  // Create options combining organization users and allowing for custom email input
  const getEmailOptions = () => {
    const orgUserOptions = (organizationUsers || []).map((user) => ({
      email: user.email,
      name: user.name,
      isOrgUser: true,
      label: `${user.name} (${user.email})`,
    }));

    return orgUserOptions;
  };

  const handleEmailChange = (event, newValue, reason) => {
    if (reason === "clear") {
      setSelectedEmails([]);
      setEmailError("");
      return;
    }

    // Handle the case when user types and presses enter or comma
    if (reason === "createOption" || reason === "selectOption") {
      const updatedEmails = [...newValue];

      // Validate all emails
      const invalidEmails = updatedEmails
        .filter((item) => typeof item === "string")
        .filter((email) => !validateEmail(email));

      if (invalidEmails.length > 0) {
        setEmailError(`Invalid email(s): ${invalidEmails.join(", ")}`);
      } else {
        setEmailError("");
      }

      setSelectedEmails(updatedEmails);
    }
  };

  const handleInputChange = (event, newInputValue, reason) => {
    if (reason === "input") {
      setInputValue(newInputValue);
    }
  };

  // Custom filter function to handle both organization users and typed emails
  const filterOptions = (options, { inputValue }) => {
    const filtered = options.filter(
      (option) =>
        option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
        option.email.toLowerCase().includes(inputValue.toLowerCase())
    );

    // If the input looks like an email and isn't already selected, add it as an option
    if (inputValue && validateEmail(inputValue)) {
      const isAlreadySelected = selectedEmails.some(
        (email) =>
          (typeof email === "string" && email === inputValue) ||
          (typeof email === "object" && email.email === inputValue)
      );

      const existsInOptions = filtered.some(
        (option) => option.email === inputValue
      );

      if (!isAlreadySelected && !existsInOptions) {
        filtered.push({
          email: inputValue,
          name: inputValue,
          isOrgUser: false,
          label: inputValue,
        });
      }
    }

    return filtered;
  };

  // Custom function to get option label
  const getOptionLabel = (option) => {
    if (typeof option === "string") {
      return option;
    }
    return option.label || option.email;
  };

  // Check if form is valid for submission
  const isFormValid = () => {
    const hasValidEmails = selectedEmails.length > 0;
    const noEmailErrors = !emailError;

    // Additional validation to ensure all selected emails are valid
    const allEmailsValid = selectedEmails.every((item) => {
      if (typeof item === "string") {
        return validateEmail(item);
      }
      return validateEmail(item.email);
    });

    return hasValidEmails && noEmailErrors && allEmailsValid;
  };

  const getReportParamsFromLocalStorage = () => {
    try {
      const storedParams = localStorage.getItem("reportCategoryParams");

      if (storedParams) {
        const parsedParams = JSON.parse(storedParams);
        console.log("Retrieved params from localStorage:", parsedParams);
        return parsedParams;
      } else {
        console.warn(
          "No reportCategoryParams found in localStorage, falling back to generated params"
        );
        return generateFallbackParams();
      }
    } catch (error) {
      console.error(
        "Error parsing reportCategoryParams from localStorage:",
        error
      );
      return generateFallbackParams();
    }
  };

  const generateFallbackParams = () => {
    const orgId = JSON.parse(localStorage.getItem("userState"))?.user
      ?.organizationId;

    const baseParams = {
      organizationId: orgId,
      dateOption: dateOptions.duration,
    };

    if (dateOptions.duration === "custom") {
      baseParams.customStartDate = dateOptions.customDateRange.startDate;
      baseParams.customEndDate = dateOptions.customDateRange.endDate;
    }

    if (selectedSiteId) {
      baseParams.siteId = selectedSiteId;
    }

    let categoryParams = {
      allVehicles: false,
      allRoutes: false,
      allDrivers: false,
      allStops: false,
      allSites: false,
      allUsers: false,
    };

    Object.entries(selectedCategories).forEach(
      ([categoryKey, categoryData]) => {
        const { isAllSelected, selectedItems } = categoryData;

        switch (categoryKey) {
          case "Vehicles":
            if (isAllSelected) {
              categoryParams.allVehicles = true;
            } else if (selectedItems && selectedItems.length > 0) {
              categoryParams.deviceIds = selectedItems.join(",");
              categoryParams.allVehicles = false;
            }
            break;
          case "Routes":
            if (isAllSelected) {
              categoryParams.allRoutes = true;
            } else if (selectedItems && selectedItems.length > 0) {
              categoryParams.routeIds = selectedItems.join(",");
              categoryParams.allRoutes = false;
            }
            break;
          case "Drivers":
            if (isAllSelected) {
              categoryParams.allDrivers = true;
            } else if (selectedItems && selectedItems.length > 0) {
              categoryParams.driverIds = selectedItems.join(",");
              categoryParams.allDrivers = false;
            }
            break;
          case "Stops":
            if (isAllSelected) {
              categoryParams.allStops = true;
            } else if (selectedItems && selectedItems.length > 0) {
              categoryParams.stopIds = selectedItems.join(",");
              categoryParams.allStops = false;
            }
            break;
          case "Users":
            if (isAllSelected) {
              categoryParams.allUsers = true;
            } else if (selectedItems && selectedItems.length > 0) {
              categoryParams.userIds = selectedItems.join(",");
              categoryParams.allUsers = false;
            }
            break;
        }
      }
    );

    return { ...baseParams, ...categoryParams };
  };

  const handleSubmit = async () => {
    setSending(true);
    try {
      // Extract all email addresses from the selected items
      const allEmails = selectedEmails.map((item) => {
        if (typeof item === "string") {
          return item;
        }
        return item.email;
      });

      if (allEmails.length === 0) {
        throw new Error("Please select at least one email address");
      }

      // Get the report parameters from localStorage
      const reportParams = getReportParamsFromLocalStorage();

      // Remove timestamp if it exists (not needed for API call)
      const { timestamp, ...apiParams } = reportParams;

      const reportTypeMapping = {
        activity: "ActivityReport",
        overspeed: "OverspeedReport",
        inventory: "inventoryReport",
        runtime: "runtimeReport",
        trip: "tripReport",
        deviation: "routeDeviationReport",
        sos: "SosReport",
        halt: "haltReport",
        entryExit: "entryExitReport",
      };

      // Build query parameters
      const queryParams = new URLSearchParams({
        email: allEmails.join(","),
        reportFormat:
          exportFormat === "csv"
            ? "excel"
            : exportFormat === "docx"
            ? "word"
            : exportFormat,
        reportType: reportCategory,
        reportSection: reportTypeMapping[reportCategory],
        ...apiParams,
      });

      console.log("Email Report Query Params:", queryParams.toString());
      console.log("Used report params from localStorage:", apiParams);
      console.log("All emails being sent to:", allEmails);

      const response = await axios.get(
        `${config.baseURL}/report/email-sharing?${queryParams.toString()}`,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status !== 200) {
        const errorData = response.data || {};
        throw new Error(errorData.message || "Failed to send report");
      }

      onClose();
      setNotification({
        open: true,
        message: "Report sent successfully",
        severity: "success",
      });
    } catch (error) {
      console.error("Error sending report:", error);
      setNotification({
        open: true,
        message:
          error.response?.data?.message ||
          error.message ||
          "Failed to send report",
        severity: "error",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "90%", sm: "500px" },
          bgcolor: "background.paper",
          borderRadius: "8px",
          boxShadow: 24,
          p: 4,
        }}
      >
        <Box
          sx={{
            background: "#B7BFCC",
            color: "#333",
            p: 2,
            borderRadius: "8px",
            mb: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontFamily: "Roboto",
              fontWeight: 600,
              fontSize: { xs: "18px", sm: "20px", md: "24px" },
            }}
          >
            Mail {reportType} Report
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            {exportFormat === "pdf" && (
              <img
                src={Pdf}
                alt="PDF"
                style={{
                  width:
                    window.innerWidth < 600
                      ? 24
                      : window.innerWidth < 900
                      ? 28
                      : 32,
                  height:
                    window.innerWidth < 600
                      ? 24
                      : window.innerWidth < 900
                      ? 28
                      : 32,
                }}
              />
            )}
            {exportFormat === "docx" && (
              <img
                src={Word}
                alt="DOCX"
                style={{
                  width:
                    window.innerWidth < 600
                      ? 24
                      : window.innerWidth < 900
                      ? 28
                      : 32,
                  height:
                    window.innerWidth < 600
                      ? 24
                      : window.innerWidth < 900
                      ? 28
                      : 32,
                }}
              />
            )}
            {exportFormat === "csv" && (
              <img
                src={Csv}
                alt="CSV"
                style={{
                  width:
                    window.innerWidth < 600
                      ? 24
                      : window.innerWidth < 900
                      ? 28
                      : 32,
                  height:
                    window.innerWidth < 600
                      ? 24
                      : window.innerWidth < 900
                      ? 28
                      : 32,
                }}
              />
            )}
          </Box>
        </Box>

        <FormControl component="fieldset" fullWidth sx={{ mt: 2 }}>
          {/* Combined Email Selection Field */}
          <Box
            sx={{
              margin: 0,
              padding: "8px 12px",
              borderRadius: "8px",
              width: "100%",
              backgroundColor: "#f5f5f5",
            }}
          >
            <Typography
              sx={{
                fontSize: "18px",
                color: "#20A8E0",
                mb: 1,
              }}
            >
              Select recipients
            </Typography>

            <Box sx={{ pl: 0, pr: 2, mb: 1 }}>
              <Autocomplete
                multiple
                freeSolo
                options={getEmailOptions()}
                getOptionLabel={getOptionLabel}
                filterOptions={filterOptions}
                value={selectedEmails}
                onChange={handleEmailChange}
                inputValue={inputValue}
                onInputChange={handleInputChange}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const tagProps = getTagProps({ index });
                    return (
                      <Chip
                        key={index}
                        label={
                          typeof option === "string" ? option : `${option.name}`
                        }
                        {...tagProps}
                        onDelete={() => {
                          const newEmails = selectedEmails.filter(
                            (_, i) => i !== index
                          );
                          setSelectedEmails(newEmails);

                          // Revalidate remaining emails
                          const invalidEmails = newEmails
                            .filter((item) => typeof item === "string")
                            .filter((email) => !validateEmail(email));

                          if (invalidEmails.length > 0) {
                            setEmailError(
                              `Invalid email(s): ${invalidEmails.join(", ")}`
                            );
                          } else {
                            setEmailError("");
                          }
                        }}
                        sx={{
                          m: 0.5,
                          backgroundColor:
                            typeof option === "string" ? "#FFF3CD" : "#E8EAF6",
                          "& .MuiChip-label": {
                            color: "#183059",
                          },
                          "& .MuiChip-deleteIcon": {
                            color: "#183059",
                            "&:hover": {
                              color: "#d32f2f",
                            },
                          },
                        }}
                      />
                    );
                  })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder="Select organization users or type external email addresses"
                    error={!!emailError}
                    helperText={
                      emailError ||
                      "Select from organization users or type email addresses. Press Enter to add custom emails."
                    }
                    sx={{
                      mt: 1,
                      "& .MuiOutlinedInput-root": {
                        minHeight: 55,
                        borderRadius: "8px",
                        border: "1px solid #183059",
                        "& .MuiAutocomplete-input": {
                          padding: "8px 12px !important",
                        },
                        "& .MuiAutocomplete-endAdornment": {
                          right: "8px",
                        },
                      },
                      "& .MuiOutlinedInput-notchedOutline": {
                        border: "none",
                      },
                      "& .MuiFormHelperText-root": {
                        color: emailError ? "#d32f2f" : "#666",
                        fontSize: "12px",
                        mt: 1,
                      },
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                      }}
                    >
                      <Typography variant="body1">
                        {option.isOrgUser ? option.name : option.email}
                      </Typography>
                      {option.isOrgUser && (
                        <Typography variant="body2" color="text.secondary">
                          {option.email}
                        </Typography>
                      )}
                    </Box>
                  </li>
                )}
              />
            </Box>
          </Box>
        </FormControl>

        <Box
          sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 3 }}
        >
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              color: "#111214",
              border: "1px solid #414958",
              textTransform: "none",
              "&:hover": { bgcolor: "#E0E0E0" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={sending || !isFormValid()}
            sx={{
              bgcolor: "#F9E719",
              color: "#111214",
              textTransform: "none",
              border: "1px solid #414958",
              "&:hover": { bgcolor: "#E0D618" },
              "&:disabled": {
                bgcolor: "#ccc",
                color: "#999",
              },
            }}
          >
            {sending ? <CircularProgress size={24} /> : "Send Report"}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
